import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Skeleton,
  Collapse,
  Avatar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useSearchParams } from "react-router-dom";
import { TV2_COLUMN_HEADER_HEIGHT } from "../../../constants/ticketV2Constants";
import { IconDetailsSplit24, IconInbox16 } from "../TicketV2/TicketV2Icons";
import { IconPageVisit16 } from "../ChatV2/ChatV2Icons";
import { msgTimeWithSeconds, chracterPosition } from "../../../utils/index";
import { colorCodesForAvatar } from "../../../constants/index";
import {
  getTrustchatGoogleMapsLoaderOptions,
  useGoogleMapsAuthFailed,
} from "../../../utils/googleMapsLoader";
import {
  selectIsTrafficUserDetailSidebarOpen,
  closeTrafficUserDetailSidebar,
} from "../../../store/features/trafficUserDetailSidebarSlice";
import { getCustomerConversationHistory } from "../../../store/features/getCustomerConversationHistory";
import { mapConversationsToRows } from "../../../utils/conversationHistoryMappers";
import ConversationsHistoryModal from "../TicketV2/ConversationsHistoryModal";
import { HistoryPreviewSkeleton } from "../TicketV2/ticketDetails/TicketDetailsSelects";

const inter = '"Inter", sans-serif';

const emptyPlaceholderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 40,
  borderRadius: "8px",
  bgcolor: "#F9FAFA",
  color: "#405261",
  fontFamily: inter,
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
};

const mapPlaceholderSx = {
  height: 184,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  textAlign: "center",
  color: "#405261",
  fontSize: 13,
  background: "#F4F6F8",
  borderRadius: "8px",
  boxSizing: "border-box",
};

function InfoField({ label, value, loading }) {
  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        sx={{ fontSize: 12, color: "#405261", lineHeight: "16px", mb: 0.5, fontFamily: inter }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton width="80%" height={20} />
      ) : (
        <Typography
          sx={{
            fontSize: 14,
            color: "#061522",
            lineHeight: "24px",
            fontFamily: inter,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value || "( no answer )"}
        </Typography>
      )}
    </Box>
  );
}

function Section({ title, open, onToggle, children }) {
  return (
    <Box sx={{ borderBottom: "1px solid #E5E9EC" }}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 0,
          border: "none",
          bgcolor: "transparent",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: inter,
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: "#061522",
            lineHeight: "16px",
            fontFamily: inter,
          }}
        >
          {title}
        </Typography>
        {open ? (
          <ExpandMoreIcon sx={{ fontSize: 18, color: "#405261" }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 18, color: "#405261" }} />
        )}
      </Box>
      <Collapse in={open} unmountOnExit={false}>
        <Stack gap={2} sx={{ pb: 2, display: open ? "flex" : "none" }}>
          {children}
        </Stack>
      </Collapse>
    </Box>
  );
}

function DetailsMap({ apiKey, center, loading }) {
  const loaderOpts = useMemo(
    () => getTrustchatGoogleMapsLoaderOptions(apiKey),
    [apiKey]
  );
  const { isLoaded, loadError } = useJsApiLoader(loaderOpts);
  const authFailed = useGoogleMapsAuthFailed();
  const [mapInstance, setMapInstance] = useState(null);

  const onLoad = useCallback((map) => {
    setMapInstance(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMapInstance(null);
  }, []);

  useEffect(() => {
    if (!mapInstance || !center?.lat || !center?.lng) return;
    try {
      mapInstance.panTo(center);
    } catch (_) {
      /* ignore pan errors while script reloads */
    }
  }, [mapInstance, center.lat, center.lng]);

  if (loadError || authFailed) {
    return (
      <Box sx={mapPlaceholderSx}>
        <Typography sx={{ fontWeight: 500, fontSize: 13, fontFamily: inter }}>
          Could not load map
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: 184,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "184px" }}
          center={center}
          zoom={10}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
          }}
        >
          <Marker position={center} />
        </GoogleMap>
      )}
      {(loading || !isLoaded) && (
        <Skeleton
          variant="rectangular"
          height={184}
          width="100%"
          sx={{ position: "absolute", inset: 0, borderRadius: "8px" }}
        />
      )}
    </Box>
  );
}

function formatLastSeen(lastSeen) {
  if (!lastSeen) return "";
  const lastSeenDate = new Date(lastSeen);
  if (Number.isNaN(lastSeenDate.getTime())) return String(lastSeen);
  const now = new Date();
  const lastSeenDay = new Date(
    lastSeenDate.getFullYear(),
    lastSeenDate.getMonth(),
    lastSeenDate.getDate()
  );
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.floor((nowDay - lastSeenDay) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1) return `${diffDays} days ago`;
  return msgTimeWithSeconds(lastSeen);
}

function parseVisitedPage(page) {
  const rawUrl = page?.url || page?.path || "";
  let title = page?.title || page?.name || rawUrl;
  let subtitle = rawUrl;
  try {
    const u = new URL(rawUrl);
    title =
      page?.title ||
      page?.name ||
      (u.pathname && u.pathname !== "/" ? u.pathname : u.href);
    subtitle = u.origin + (u.pathname || "");
  } catch (_) {
    title = page?.title || page?.name || rawUrl || "Page";
    subtitle = page?.referrer || rawUrl || "—";
  }
  return { title, subtitle, key: page?._id || rawUrl || title };
}

function UserDetail({ selectedUser, loading = false }) {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectIsTrafficUserDetailSidebarOpen);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openSections, setOpenSections] = useState({
    location: true,
    info: true,
    visited: true,
    history: true,
    apps: false,
  });
  const [showChatHistoryModal, setShowChatHistoryModal] = useState(false);
  const [chatHistoryPreview, setChatHistoryPreview] = useState([]);
  const [chatHistoryTotal, setChatHistoryTotal] = useState(0);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);

  const apiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();
  const user = selectedUser || {};
  const isLoading = !!loading || (!selectedUser && isOpen);

  const center = useMemo(
    () => ({
      lat: Number(user?.location?.latitude ?? user?.lat) || 40.7128,
      lng: Number(user?.location?.longitude ?? user?.lng) || -74.006,
    }),
    [user?.location?.latitude, user?.location?.longitude, user?.lat, user?.lng]
  );

  const locationString = user?.location
    ? user?.location?.city || user?.location?.country || "Unknown"
    : user?.location_string || "Unknown";

  const displayName = user?.name?.includes("Vistor")
    ? user?.ip_address
    : user?.name;

  const returningLabel =
    user?.visit_count != null || user?.chat_count != null || user?.ticket_count != null
      ? `${user?.visit_count ?? 0} visits, ${user?.chat_count ?? 0} chats, ${user?.ticket_count ?? 0} tickets.`
      : "";

  const visitedPages = Array.isArray(user?.visited_pages) ? user.visited_pages : [];

  const historyCustomerId = user?.id || user?._id || "";
  const historyCustomerName =
    (displayName && String(displayName)) || user?.name || "Customer";

  const toggle = (key) => setOpenSections((s) => ({ ...s, [key]: !s[key] }));

  const handleClose = useCallback(() => {
    dispatch(closeTrafficUserDetailSidebar());
    const params = new URLSearchParams(searchParams.toString());
    params.delete("userId");
    setSearchParams(params, { replace: false });
  }, [dispatch, searchParams, setSearchParams]);

  useEffect(() => {
    if (!historyCustomerId) {
      setChatHistoryPreview([]);
      setChatHistoryTotal(0);
      setChatHistoryLoading(false);
      return undefined;
    }
    if (showChatHistoryModal) return undefined;

    let cancelled = false;
    setChatHistoryLoading(true);
    dispatch(
      getCustomerConversationHistory({
        customerId: historyCustomerId,
        page: 1,
        limit: 5,
      })
    )
      .then((res) => {
        if (cancelled) return;
        if (res.meta.requestStatus === "fulfilled") {
          const data = res.payload?.data || {};
          setChatHistoryPreview(mapConversationsToRows(data.conversations));
          setChatHistoryTotal(data.summary?.total || data.pagination?.total || 0);
        } else {
          setChatHistoryPreview([]);
          setChatHistoryTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setChatHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, historyCustomerId, showChatHistoryModal]);

  useEffect(() => {
    setChatHistoryPreview([]);
    setChatHistoryTotal(0);
  }, [historyCustomerId]);

  if (!isOpen) return null;

  return (
    <>
      <ConversationsHistoryModal
        isOpen={showChatHistoryModal}
        onClose={() => setShowChatHistoryModal(false)}
        customerId={historyCustomerId}
        customerName={historyCustomerName}
      />

      <Box
        onClick={handleClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(6, 21, 34, 0.2)",
          zIndex: 1200,
        }}
      />
      <Box
        component="aside"
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: 280,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: inter,
          boxSizing: "border-box",
          bgcolor: "#FFF",
          borderLeft: "1px solid #E5E9EC",
          boxShadow: "0 4px 80px 0 rgba(6, 21, 34, 0.10)",
          zIndex: 1201,
          animation: "trafficUserDetailSlideIn 0.25s ease-out",
          "@keyframes trafficUserDetailSlideIn": {
            from: { transform: "translateX(100%)" },
            to: { transform: "translateX(0)" },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            height: TV2_COLUMN_HEADER_HEIGHT,
            minHeight: TV2_COLUMN_HEADER_HEIGHT,
            boxSizing: "border-box",
            flexShrink: 0,
            px: 2,
            borderBottom: "1px solid #E5E9EC",
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: "#061522",
              fontFamily: inter,
              lineHeight: "26px",
            }}
          >
            Details
          </Typography>
          <IconButton
            size="small"
            sx={{ p: 0.5, color: "#405261" }}
            aria-label="Hide details panel"
            onClick={handleClose}
          >
            <IconDetailsSplit24 />
          </IconButton>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            px: 2,
            py: 1,
          }}
        >
          <Section
            title="Location"
            open={openSections.location}
            onToggle={() => toggle("location")}
          >
            {!apiKey ? (
              <Box sx={mapPlaceholderSx}>
                <Typography sx={{ fontWeight: 500, fontSize: 13, fontFamily: inter }}>
                  Map unavailable
                </Typography>
              </Box>
            ) : (
              <DetailsMap apiKey={apiKey} center={center} loading={isLoading} />
            )}
          </Section>

          <Section title="Info" open={openSections.info} onToggle={() => toggle("info")}>
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 0.5 }}>
              {isLoading ? (
                <>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="60%" height={18} />
                    <Skeleton width="80%" height={16} />
                  </Box>
                </>
              ) : (
                <>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: 16,
                      fontWeight: 500,
                      bgcolor:
                        colorCodesForAvatar[
                          chracterPosition(
                            (displayName ? String(displayName).substring(0, 1) : "V").toUpperCase()
                          )
                        ],
                    }}
                  >
                    {(displayName ? String(displayName).charAt(0) : "V").toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#061522",
                        fontFamily: inter,
                        lineHeight: "18px",
                        textTransform: "capitalize",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {displayName || "( no answer )"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#405261",
                        fontFamily: inter,
                        lineHeight: "18px",
                        textTransform: "lowercase",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {user?.email || ""}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
            <InfoField label="Name" value={displayName} loading={isLoading} />
            <InfoField label="Email" value={user?.email} loading={isLoading} />
            <InfoField
              label="Time"
              value={
                user?.updatedAt || user?.local_time
                  ? `${msgTimeWithSeconds(user?.updatedAt || user?.local_time)} local time`
                  : ""
              }
              loading={isLoading}
            />
            <InfoField label="Location" value={locationString} loading={isLoading} />
            <InfoField
              label="Returning Customer"
              value={returningLabel}
              loading={isLoading}
            />
            <InfoField
              label="Last Seen"
              value={formatLastSeen(user?.last_seen)}
              loading={isLoading}
            />
            <InfoField label="IP Address" value={user?.ip_address} loading={isLoading} />
            <InfoField label="OS/Device" value={user?.os} loading={isLoading} />
            <InfoField label="Browser" value={user?.browser} loading={isLoading} />
          </Section>

          <Section
            title="Visited Pages"
            open={openSections.visited}
            onToggle={() => toggle("visited")}
          >
            {isLoading ? (
              <Stack gap={1}>
                <Skeleton width="70%" height={20} />
                <Skeleton width="90%" height={16} />
              </Stack>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#405261",
                    lineHeight: "24px",
                    fontFamily: inter,
                  }}
                >
                  {visitedPages.length
                    ? `Viewed ${visitedPages.length} page${visitedPages.length === 1 ? "" : "s"}`
                    : "No pages viewed"}
                </Typography>
                {visitedPages.slice(0, 4).map((page) => {
                  const { title, subtitle, key } = parseVisitedPage(page);
                  return (
                    <Stack key={key} direction="row" gap={1} alignItems="flex-start">
                      <Box sx={{ mt: "2px", flexShrink: 0 }}>
                        <IconPageVisit16 />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#061522",
                            lineHeight: "16px",
                            fontFamily: inter,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#405261",
                            lineHeight: "24px",
                            fontFamily: inter,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {subtitle}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })}
              </>
            )}
          </Section>

          <Section
            title="History"
            open={openSections.history}
            onToggle={() => toggle("history")}
          >
            {chatHistoryLoading && chatHistoryPreview.length === 0 ? (
              <HistoryPreviewSkeleton rows={5} />
            ) : chatHistoryPreview.length > 0 ? (
              <Stack gap={1}>
                {chatHistoryPreview.map((row) => (
                  <Stack
                    key={row.id}
                    direction="row"
                    alignItems="center"
                    gap={1}
                    onClick={() => setShowChatHistoryModal(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowChatHistoryModal(true);
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "8px",
                      "&:hover": { bgcolor: "#F9FAFA" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconInbox16 />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#405261",
                        fontFamily: inter,
                        fontWeight: 400,
                        lineHeight: "18px",
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.subject}
                    </Typography>
                  </Stack>
                ))}
                <Typography
                  component="button"
                  type="button"
                  onClick={() => setShowChatHistoryModal(true)}
                  sx={{
                    mt: 0.5,
                    p: 0,
                    border: 0,
                    background: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: inter,
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: "#148DFD",
                    textDecoration: "underline",
                    textDecorationSkipInk: "none",
                    textUnderlineOffset: "4px",
                    "&:hover": { color: "#0B7AD9" },
                  }}
                >
                  View All {chatHistoryTotal}
                </Typography>
              </Stack>
            ) : (
              <Box sx={emptyPlaceholderSx}>No History</Box>
            )}
          </Section>

          <Section title="Apps" open={openSections.apps} onToggle={() => toggle("apps")}>
            <Typography sx={{ fontSize: 12, color: "#8B97A2", fontFamily: inter }}>
              No apps connected.
            </Typography>
          </Section>
        </Box>
      </Box>
    </>
  );
}

UserDetail.propTypes = {
  selectedUser: PropTypes.object,
  loading: PropTypes.bool,
};

export default UserDetail;
